<script setup lang="ts">
    import { computed, onMounted, watch, ref, type Ref } from 'vue'
    import { useRoute, onBeforeRouteLeave } from 'vue-router'

    import { SavingState } from '@common/Common'
    import CorporateNavigation from '@common/Components/CorporateNavigation.vue'
    import EditableValueSeriesSheet from '@common/Components/EditableValueSeriesSheet.vue'
    import SaveButton from '@common/Components/SaveButton.vue'
    import ValueSeriesTable from '@Quest/_Common/Components/ValueSeriesTable.vue'

    import { useResourceUnitCostsQueryV1 } from '@Quest/Resources/queries-vue-composables'
    import { useUpdateResourceUnitCostsCommandV1 } from '@Quest/Strategies/commands-vue-composables'

    import type { ResourceUnitCostsReadModel } from '@Quest/Resources/readmodels'

    const route = useRoute()

    const workspaceId = computed(() => Number(route.params.workspaceId ?? 0))
    const strategyId = computed(() => String(route.params.strategyId ?? ''))

    // --------------------
    // Query
    // --------------------

    const {
        runResourceUnitCostsQueryV1,
        modelResourceUnitCostsQueryV1
    } = useResourceUnitCostsQueryV1(workspaceId, strategyId)

    const rm = modelResourceUnitCostsQueryV1 as Ref<ResourceUnitCostsReadModel | null>

    // --------------------
    // Command
    // --------------------

    const {
        invokeUpdateResourceUnitCostsCommandV1,
        succeedUpdateResourceUnitCostsCommandV1
    } = useUpdateResourceUnitCostsCommandV1(workspaceId, strategyId)

    // --------------------
    // State
    // --------------------

    const isEditing = ref(false)
    const savingState = ref<SavingState>(SavingState.Saved)

    // --------------------
    // Computed
    // --------------------

    const title = computed(() =>
        `${rm.value?.Title} (${rm.value?.CurrencyLabel})`
    )

    const stopEditingLabel = computed(() =>
        savingState.value === SavingState.Pending
            ? "Cancel editing"
            : "Back"
    )

    const inputSeries = computed(() =>
        rm.value?.Series.filter(a => !a.IsTotal) ?? []
    )

    // --------------------
    // Methods
    // --------------------

    function startEditing()
    {
        isEditing.value = true
    }

    function cancelEditing()
    {
        if (savingState.value === SavingState.Saved)
        {
            isEditing.value = false
        }
        else if (
            savingState.value === SavingState.Pending &&
            confirm("You have unsaved changes. Are you sure you want to cancel editing?")
        )
        {
            savingState.value = SavingState.Saved
            isEditing.value = false
        }
    }

    function onDataChange()
    {
        savingState.value = SavingState.Pending
    }

    const sheet = ref<any>(null)

    function handleSaveCommand()
    {
        const entries = sheet.value?.getEntriesForSave()

        invokeUpdateResourceUnitCostsCommandV1({
            fromYear: rm.value!.FromYear,
            entries
        })

        savingState.value = SavingState.InProgress
    }

    // --------------------
    // Lifecycle
    // --------------------

    onMounted(() =>
    {
        document.title = "Resource unit costs | TransparentChoice"
        runResourceUnitCostsQueryV1()
    })

    // --------------------
    // Command Success Watcher
    // --------------------

    watch(succeedUpdateResourceUnitCostsCommandV1, (current, previous) =>
    {
        if (current && !previous)
        {
            runResourceUnitCostsQueryV1()

            if (savingState.value === SavingState.InProgress)
            {
                savingState.value = SavingState.Saved
            }
        }
    })

    // --------------------
    // Route Leave Guard
    // --------------------

    onBeforeRouteLeave((to, from, next) =>
    {
        if (savingState.value === SavingState.Pending)
        {
            if (confirm("You have unsaved changes. Do you want to save them before leaving?"))
            {
                handleSaveCommand()
                next()
            } else
            {
                next()
            }
        } else
        {
            next()
        }
    })
</script>

<template>
    <div>
        <CorporateNavigation ActivePage="resources"></CorporateNavigation>


        <div v-if="rm" class="container-fluid" style="margin-top: 80px; padding-top: 40px;">
            <div class="row">
                <div class="col-lg-6">
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">Unit costs</h1>
                </div>
                <div class="col-lg-6 text-right align-self-end">
                    <SaveButton v-if="isEditing" class="mr-1" :savingState="savingState" @saveChanges="handleSaveCommand()"></SaveButton>
                    <button v-if="isEditing" class="btn btn-outline-secondary" style="padding: 0.7em 2em;" @click="cancelEditing">{{stopEditingLabel}}</button>

                    <button class="btn btn-success" style="padding: 0.7em 2em;" v-if="!isEditing" @click="startEditing">Edit unit costs</button>
                </div>
            </div>

            <hr style="border-color: #bbb; margin: 20px 0 60px;" />

            <div class="alert alert-info" role="alert">
                {{rm?.FiscalYearRangeLabel}}
            </div>

            <div v-if="isEditing">

                <div class="alert alert-warning" role="alert">
                    Warning: Formula entered here will save as values.
                </div>

                <EditableValueSeriesSheet ref="sheet" :propTitle="title" :propSeries="inputSeries" :propColumns="rm.Columns" @dataChange="onDataChange" />
            </div>
            <div v-else>
                <div class="toolbar">
                    <div>
                        <div class="btn-group" role="group">
                            <a class="btn btn-outline-secondary" v-sanitizedHref="`/#/enterprise/resources/${workspaceId}/${strategyId}`">
                                Resources
                            </a>
                            <span class="btn btn-secondary disabled">
                                Unit costs
                            </span>
                        </div>
                    </div>
                    <div></div>
                </div>
                <ValueSeriesTable :propTitle="title" :propSeries="rm.Series" :propColumns="rm.Columns" />
            </div>
        </div>
    </div>

</template>

<style scoped>
    .toolbar {
        background-color: #f8f8f8; /* Light gray background for the toolbar */
        padding: 10px 15px; /* Padding around the toolbar */
        border: 1px solid #e4e4e4; /* A light border to give separation */
        display: flex; /* Use flex to give layout flexibility */
        align-items: center; /* Center-align items vertically */
        justify-content: space-between; /* Separate items */
    }
</style>


